import { MagnifyingGlass, Warning } from "@phosphor-icons/react";
import clsx from "clsx";
import React, { useState } from "react";
import { FieldError } from "react-hook-form";
import FormInputMessage from "./FormInputError";
import FormInputLabel from "./FormInputLabel";
import InfoCard from "./InfoCard";

export type FormTextInputProps = Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "type" | "className"
> & {
    label?: string;
    error?: FieldError;
    helpText?: string;
    infoText?: string;
    required?: boolean;
    tooltip?: string;
    type?: "text" | "number";
    labelClassName?: string;
    StartAdornmentIcon?: typeof MagnifyingGlass;
    fillStartAdornment?: boolean;
    className?: string;
};

const FormTextInput = React.forwardRef<HTMLInputElement, FormTextInputProps>(
    (
        {
            id,
            label,
            error,
            helpText,
            infoText,
            required,
            tooltip,
            type = "text",
            labelClassName,
            StartAdornmentIcon,
            fillStartAdornment,
            className,
            disabled,
            ...inputProps
        },
        ref
    ) => {
        const [moreInfoIsExpanded, setMoreInfoIsExpanded] = useState(false);
        const moreInfoLabelId = `${label}-more-info`;
        const moreInfoPanelId = `${label}-more-info-panel`;

        return (
            <div className={clsx("w-full", className)}>
                <div className="flex justify-between">
                    {label && (
                        <FormInputLabel
                            htmlFor={id}
                            required={required}
                            className={labelClassName}
                            tooltipText={tooltip}
                        >
                            {label}
                        </FormInputLabel>
                    )}
                    {infoText && (
                        <button
                            className="text-sm underline hover:font-semibold"
                            id={moreInfoLabelId}
                            aria-expanded={moreInfoIsExpanded}
                            aria-controls={moreInfoPanelId}
                            onClick={() =>
                                setMoreInfoIsExpanded((prev) => !prev)
                            }
                            aria-label={
                                moreInfoIsExpanded ? "hide info" : undefined
                            }
                            type="button"
                        >
                            <span className="sr-only">{label} </span>info
                        </button>
                    )}
                </div>
                <div className="relative">
                    <input
                        {...inputProps}
                        id={id}
                        type={type}
                        ref={ref}
                        aria-required={required}
                        className={clsx(
                            "border border-surface-dark-5 bg-surface-light w-full h-[3.25rem] px-4 rounded transition-colors",
                            "focus:bg-surface-light focus:outline-none focus:ring-2 focus:ring-soft-green-80",
                            "hover:bg-surface-light-1",
                            error &&
                                "bg-red-95 outline-red-40 border-red-40 focus:ring-red-40 outline outline-2 pr-10",
                            disabled && "opacity-40 hover:bg-surface-light",
                            StartAdornmentIcon && "pl-12"
                        )}
                        aria-invalid={Boolean(error)}
                        aria-describedby={helpText ?? undefined}
                        disabled={disabled}
                        onWheel={(e) => e.currentTarget.blur()}
                    />
                    {StartAdornmentIcon && (
                        <StartAdornmentIcon
                            weight={fillStartAdornment ? "fill" : undefined}
                            className="pointer-events-none w-6 h-6 absolute top-1/2 transform -translate-y-1/2 left-3"
                        />
                    )}
                    {error && (
                        <Warning
                            aria-hidden="true"
                            className="pointer-events-none w-5 h-5 absolute top-1/2 transform -translate-y-1/2 right-3 text-red-40"
                            weight="fill"
                        />
                    )}
                </div>
                <FormInputMessage error={error} helpText={helpText} id={id} />
                {infoText && (
                    <InfoCard
                        className="p-3 mt-4"
                        id={moreInfoPanelId}
                        aria-labelledby={moreInfoLabelId}
                        hidden={!moreInfoIsExpanded}
                    >
                        {label && <h3 className="font-bold mb-1">{label}</h3>}
                        {infoText}
                    </InfoCard>
                )}
            </div>
        );
    }
);

export default FormTextInput;
